import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Share2, Copy, Check, RefreshCw, Gift, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";

/**
 * Sharable wishlist controls for the Account page.
 * Handles: enable/disable, copy link, regenerate token, share message.
 */
export default function WishlistShare({ hasItems }) {
  const [state, setState] = useState({ enabled: false, share_token: null, share_name: "" });
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");

  useEffect(() => {
    api.get("/wishlist/share").then(r => {
      setState(r.data);
      setName(r.data.share_name || "");
    }).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  const shareUrl = state.share_token
    ? `${window.location.origin}/wishlist/${state.share_token}`
    : null;

  const enable = async () => {
    setLoading(true);
    try {
      const r = await api.post("/wishlist/share", { share_name: name || undefined, share_message: message || undefined });
      setState(prev => ({ ...prev, enabled: true, share_token: r.data.share_token, share_name: r.data.share_name }));
      toast.success("Wishlist share link created");
    } catch { toast.error("Could not create share link"); }
    finally { setLoading(false); }
  };

  const disable = async () => {
    setLoading(true);
    try {
      await api.delete("/wishlist/share");
      setState(prev => ({ ...prev, enabled: false }));
      toast.success("Share link disabled");
    } catch { toast.error("Could not disable"); }
    finally { setLoading(false); }
  };

  const regenerate = async () => {
    if (!window.confirm("Old link will stop working. Continue?")) return;
    setLoading(true);
    try {
      const r = await api.post("/wishlist/share/regenerate");
      setState(prev => ({ ...prev, enabled: true, share_token: r.data.share_token }));
      toast.success("New link generated");
    } catch { toast.error("Could not regenerate"); }
    finally { setLoading(false); }
  };

  const copy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — please select and copy manually");
    }
  };

  return (
    <div className="bg-gradient-to-br from-[#FEF3E2] via-white to-[#F5DEC7]/50 border border-[#D4AF37]/30 rounded-3xl p-6 md:p-7 shadow-sm" data-testid="wishlist-share">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#D4AF37] flex items-center justify-center shrink-0 shadow-lg shadow-[#D4AF37]/30">
          <Gift className="w-6 h-6 text-[#3B2F2F]"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="uppercase text-[10px] tracking-[0.3em] text-[#8B7355] mb-1">Perfect for festive season</div>
          <h3 className="font-serif-display text-2xl md:text-3xl leading-tight">Share your wishlist as a gift hint</h3>
          <p className="text-sm text-stone-600 mt-2">Send this link to friends and family so they know exactly what to gift you.</p>
        </div>
      </div>

      {!state.enabled ? (
        <div className="mt-6 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-outfit font-semibold mb-1.5">Your name (as it appears on the link)</div>
            <Input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Aditi" maxLength={40} data-testid="share-name"/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-outfit font-semibold mb-1.5">Personal note <span className="text-stone-400 lowercase">(optional)</span></div>
            <Textarea value={message} onChange={e=>setMessage(e.target.value)} rows={2} maxLength={200}
                      placeholder="e.g. Hint hint — Diwali is around the corner 😉"
                      data-testid="share-message"/>
          </div>
          <button onClick={enable} disabled={loading || !hasItems} data-testid="enable-share"
                  className="w-full mt-2 bg-[#3B2F2F] text-white rounded-full py-3 font-medium hover:bg-black disabled:bg-stone-400 inline-flex items-center justify-center gap-2">
            <Share2 className="w-4 h-4"/> {hasItems ? "Create shareable link" : "Add wishlist items first"}
          </button>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          <div className="bg-white border-2 border-dashed border-[#D4AF37] rounded-2xl p-4 flex items-center gap-3">
            <LinkIcon className="w-4 h-4 text-[#8B7355] shrink-0"/>
            <div className="flex-1 min-w-0 font-outfit text-sm text-[#3B2F2F] truncate" data-testid="share-url">{shareUrl}</div>
            <button onClick={copy} data-testid="copy-link"
                    className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${
                      copied ? "bg-[#2E4A35] text-white" : "bg-[#3B2F2F] text-white hover:bg-black"}`}>
              {copied ? <><Check className="w-3.5 h-3.5"/>Copied</> : <><Copy className="w-3.5 h-3.5"/>Copy</>}
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <a href={shareUrl} target="_blank" rel="noopener noreferrer" data-testid="preview-share"
               className="text-xs text-[#3B2F2F] hover:text-[#D4AF37] font-medium underline">Preview link</a>
            <span className="text-xs text-stone-400">·</span>
            <button onClick={regenerate} disabled={loading} className="text-xs text-stone-600 hover:text-[#3B2F2F] inline-flex items-center gap-1" data-testid="regen-share">
              <RefreshCw className="w-3 h-3"/> Regenerate
            </button>
            <span className="text-xs text-stone-400">·</span>
            <button onClick={disable} disabled={loading} className="text-xs text-[#993322] hover:underline" data-testid="disable-share">
              Disable sharing
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2 pt-2">
            <ShareBtn label="WhatsApp" color="#25D366" href={`https://wa.me/?text=${encodeURIComponent("Take a peek at my coffee wishlist ☕ " + shareUrl)}`}/>
            <ShareBtn label="X / Twitter" color="#000" href={`https://twitter.com/intent/tweet?text=${encodeURIComponent("My coffee wishlist ☕")}&url=${encodeURIComponent(shareUrl)}`}/>
            <ShareBtn label="Email" color="#3B2F2F" href={`mailto:?subject=${encodeURIComponent("My coffee wishlist")}&body=${encodeURIComponent("Hey! Here's my DailyBeans wishlist:\n\n" + shareUrl)}`}/>
          </div>
        </div>
      )}
    </div>
  );
}

function ShareBtn({ label, color, href }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
       className="text-xs font-outfit font-semibold text-white rounded-full py-2 text-center hover:opacity-90 transition-opacity"
       style={{background: color}}>
      {label}
    </a>
  );
}
